import { socialApiClient } from '@/shared/api-client/social-api-client';
import z from 'zod';

import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter';
import type { Creator } from '../discovery-schema';

export const listCreatorDiscoverySearchResultsRequestSchema = z.object({
  attempt: z.number().int().min(1),
  show_saved: z.boolean().optional(),
});

export const discoveryMatchedPostSchema = z
  .object({
    id: z.string().optional(),
    poster_url: z.string().nullable().optional(),
    video_url: z.string().nullable().optional(),
    source_url: z.string().nullable().optional(),
    content: z.string().nullable().optional(),
    ai_matched_features: z.array(z.string()).nullable().optional(),
    engagement: z.record(z.string(), z.unknown()).nullable().optional(),
    date: z.string().nullable().optional(),
  })
  .passthrough();

export const creatorSearchItemSchema = z
  .object({
    search_id: z.string(),
    creator_id: z.string(),
    matched_keywords: z.array(z.string()),
    matched_post_ids: z.array(z.string()),
    post_categories: z.array(z.string()),
    match_score: z.number().nullable().optional(),
    is_saved: z.boolean().nullable().optional(),
    platform: z.enum(SOURCE_PLATFORM_TYPE).nullable().optional(),
    username: z.string().nullable().optional(),
    nickname: z.string().nullable().optional(),
    profile_image_url: z.string().nullable().optional(),
    followers: z.number().nullable().optional(),
    average_er: z.number().nullable().optional(),
    matched_posts: z.array(discoveryMatchedPostSchema).nullable().optional(),
    excluded_date: z.string().nullable().optional(),
    saved_at: z.string().nullable().optional(),
  })
  .passthrough();

export type ListCreatorDiscoverySearchResultsRequest = z.infer<
  typeof listCreatorDiscoverySearchResultsRequestSchema
>;
export type DiscoveryMatchedPost = z.infer<typeof discoveryMatchedPostSchema>;
export type CreatorSearchItem = z.infer<typeof creatorSearchItemSchema>;
export type SearchResultListResponse = {
  complete: boolean;
  total_count: number;
  creators: Array<Creator>;
};

export async function listCreatorDiscoverySearchResults(
  sessionId: string,
  searchId: string,
  request: ListCreatorDiscoverySearchResultsRequest,
  signal?: AbortSignal,
): Promise<SearchResultListResponse> {
  const searchParams = listCreatorDiscoverySearchResultsRequestSchema.parse(request);
  const response = await socialApiClient.get(
    `discovery-sessions/${sessionId}/search/${searchId}/results`,
    {
      searchParams: {
        ...searchParams,
        full_matched_only: false,
      },
      signal,
    },
  );

  return await response.json();
}
